import { useState, useEffect, useMemo } from 'react';
import { client } from '@/api/client';
import { useAuth } from '@/lib/AuthContext';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { cn } from '@/lib/utils';

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr) - new Date()) / 86400000);
};

// null = not expiring soon at all; 'expired' = past due; 'soon' = within 30 days.
const expiryBucket = (dateStr) => {
  const d = daysUntil(dateStr);
  if (d === null) return null;
  if (d <= 0) return 'expired';
  if (d <= 30) return 'soon';
  return null;
};

function weekStart(dateStr) {
  const d = new Date(`${dateStr}T00:00:00`);
  d.setDate(d.getDate() - d.getDay()); // back to Sunday
  return d.toISOString().split('T')[0];
}

function SummaryCard({ title, subtitle, columns, rows }) {
  return (
    <div className="glass-card p-4">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      {subtitle && <p className="text-[11px] text-muted-foreground mt-0.5 mb-2">{subtitle}</p>}
      <table className="w-full text-xs mt-2">
        <thead>
          <tr className="border-b border-border">
            <th className="text-left pb-2 text-muted-foreground font-medium">Report</th>
            {columns.map(c => <th key={c} className="text-right pb-2 text-muted-foreground font-medium">{c}</th>)}
          </tr>
        </thead>
        <tbody className="divide-y divide-border/50">
          {rows.map(r => (
            <tr
              key={r.label}
              onClick={r.onClick}
              className={cn("transition-colors", r.onClick && "cursor-pointer hover:bg-white/5")}
            >
              <td className="py-2 text-foreground">{r.label}</td>
              {r.values.map((v, i) => (
                <td key={i} className={cn("py-2 text-right font-semibold", i === 0 && v > 0 && r.highlightFirst ? 'text-amber-400' : 'text-foreground')}>
                  {v}
                </td>
              ))}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={columns.length + 1} className="py-4 text-center text-muted-foreground">Nothing to show.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// Dispatch/ops home dashboard -- the carrier's own fleet + loads at a
// glance (no broker concepts like payables to a sub-carrier; this
// carrier runs its own trucks). Mirrors the shape of the KPI-table home
// screens common in TMS/broker platforms, adapted to what this carrier
// actually tracks.
export default function HomeDashboard({ onOpenLoads, onOpenDriver, onOpenAsset, onOpenInvoices }) {
  const { user } = useAuth();
  const [loads, setLoads] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [assets, setAssets] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      client.entities.Load.list('-delivery_date', 1000),
      client.entities.Invoice.list('-created_date', 1000),
      client.entities.Asset.list('unit_number', 200),
      client.entities.DriverProfile.filter({ approval_status: 'Approved' }, 'full_name', 200),
      client.entities.Trip.list('-created_date', 500),
    ]).then(([l, i, a, d, t]) => {
      setLoads(l); setInvoices(i); setAssets(a); setDrivers(d); setTrips(t); setLoading(false);
    });
  }, []);

  const myLoads = useMemo(() => loads.filter(l => l.created_by === user?.id), [loads, user?.id]);
  const countBy = (arr, status) => arr.filter(l => l.status === status).length;

  const loadSummaryRows = [
    { label: 'Available', values: [countBy(loads, 'Available'), countBy(myLoads, 'Available')], onClick: () => onOpenLoads?.('Available') },
    { label: 'Booked', values: [countBy(loads, 'Booked'), countBy(myLoads, 'Booked')], onClick: () => onOpenLoads?.('Booked') },
    { label: 'In Transit', values: [countBy(loads, 'In Transit'), countBy(myLoads, 'In Transit')], onClick: () => onOpenLoads?.('In Transit') },
    { label: 'Delivered', values: [countBy(loads, 'Delivered'), countBy(myLoads, 'Delivered')], onClick: () => onOpenLoads?.('Delivered') },
  ];

  const today = new Date().toISOString().split('T')[0];
  const runningLate = loads.filter(l => ['Booked', 'In Transit'].includes(l.status) && l.delivery_date && l.delivery_date < today);
  const myRunningLate = runningLate.filter(l => l.created_by === user?.id);
  const missingBOL = loads.filter(l => l.status === 'Delivered' && !l.ebol_url);
  const myMissingBOL = missingBOL.filter(l => l.created_by === user?.id);
  const tempDiscrepancy = trips.filter(t =>
    t.equipment_type === 'Reefer' && t.temp_actual != null && t.temp_min != null && t.temp_max != null &&
    (t.temp_actual < t.temp_min || t.temp_actual > t.temp_max)
  );
  const myTempDiscrepancy = tempDiscrepancy.filter(t => t.dispatcher_user_id === user?.id);

  const attentionRows = [
    { label: 'Running Late', values: [runningLate.length, myRunningLate.length], highlightFirst: true, onClick: () => onOpenLoads?.('__late') },
    { label: 'Missing BOL', values: [missingBOL.length, myMissingBOL.length], highlightFirst: true, onClick: () => onOpenLoads?.('Delivered') },
    { label: 'Temp Discrepancy', values: [tempDiscrepancy.length, myTempDiscrepancy.length], highlightFirst: true },
  ];

  const incomplete = loads.filter(l => l.status === 'Delivered');
  const released = loads.filter(l => l.status === 'Invoiced');
  const unpaidInvoices = invoices.filter(i => i.status === 'Unpaid');
  const receivablesTotal = unpaidInvoices.reduce((s, i) => s + (i.balance_due ?? i.amount ?? 0), 0);

  const billRows = [
    { label: 'Incomplete (awaiting release)', values: [incomplete.length], onClick: () => onOpenLoads?.('Delivered') },
    { label: 'Released', values: [released.length], onClick: () => onOpenLoads?.('Invoiced') },
    { label: 'Receivables', values: [unpaidInvoices.length], onClick: () => onOpenInvoices?.() },
  ];

  const assignedEmails = new Set(assets.filter(a => a.assigned_driver_email).map(a => a.assigned_driver_email.toLowerCase()));
  const unassignedDrivers = drivers.filter(d => d.user_email && !assignedEmails.has(d.user_email.toLowerCase()));
  const activeAssets = assets.filter(a => a.is_active !== false);
  const unassignedTrucks = activeAssets.filter(a => a.asset_type === 'Truck' && !a.assigned_driver_email);
  const unassignedTrailers = activeAssets.filter(a => a.asset_type === 'Trailer' && !a.assigned_driver_email);

  const unassignedRows = [
    { label: 'Drivers', values: [unassignedDrivers.length] },
    { label: 'Trucks', values: [unassignedTrucks.length] },
    { label: 'Trailers', values: [unassignedTrailers.length] },
  ];

  let driverExpired = 0, driverSoon = 0, truckExpired = 0, truckSoon = 0, trailerExpired = 0, trailerSoon = 0;
  drivers.forEach(d => {
    [d.cdl_expiration_date, d.med_card_expiration_date].forEach(date => {
      const b = expiryBucket(date);
      if (b === 'expired') driverExpired++; else if (b === 'soon') driverSoon++;
    });
  });
  activeAssets.forEach(a => {
    [a.plate_expiration, a.registration_expiration, a.annual_inspection_due].forEach(date => {
      const b = expiryBucket(date);
      if (!b) return;
      if (a.asset_type === 'Truck') { if (b === 'expired') truckExpired++; else truckSoon++; }
      else if (a.asset_type === 'Trailer') { if (b === 'expired') trailerExpired++; else trailerSoon++; }
    });
  });

  const safetyRows = [
    { label: 'Driver', values: [driverExpired, driverSoon], highlightFirst: true, onClick: () => onOpenDriver?.() },
    { label: 'Truck', values: [truckExpired, truckSoon], highlightFirst: true, onClick: () => onOpenAsset?.() },
    { label: 'Trailer', values: [trailerExpired, trailerSoon], highlightFirst: true, onClick: () => onOpenAsset?.() },
  ];

  const weeklyRevenue = useMemo(() => {
    const map = {};
    loads.forEach(l => {
      if (!l.delivery_date || !l.rate) return;
      const wk = weekStart(l.delivery_date);
      map[wk] = (map[wk] || 0) + l.rate;
    });
    return Object.entries(map)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .slice(-12)
      .map(([wk, revenue]) => ({
        week: new Date(`${wk}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: '2-digit' }),
        revenue: Math.round(revenue),
      }));
  }, [loads]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-6 h-6 border-2 border-border border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <SummaryCard title="Load Summary" columns={['All Loads', 'My Loads']} rows={loadSummaryRows} />
        <SummaryCard title="Requires Attention" columns={['All', 'Mine']} rows={attentionRows} />
        <SummaryCard title="Bill Summary" columns={['Loads']} rows={billRows} subtitle={receivablesTotal > 0 ? `$${receivablesTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })} outstanding` : undefined} />
        <SummaryCard title="Unassigned Assets" columns={['Count']} rows={unassignedRows} />
        <SummaryCard title="Safety Summary" columns={['Expired', 'Expiring Soon']} rows={safetyRows} />
      </div>

      <div className="glass-card p-4">
        <h3 className="text-sm font-semibold text-foreground">Weekly Loads</h3>
        <p className="text-[11px] text-muted-foreground mt-0.5 mb-3">Weekly revenue based on delivery date. Dates reflect the week beginning date.</p>
        {weeklyRevenue.length === 0 ? (
          <p className="text-xs text-muted-foreground py-8 text-center">No delivered load revenue yet.</p>
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={weeklyRevenue} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="weeklyRevenueFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="week" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={{ stroke: 'hsl(var(--border))' }} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip
                  contentStyle={{ background: 'hsl(var(--background))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }}
                  labelStyle={{ color: 'hsl(var(--foreground))' }}
                  formatter={(v) => [`$${v.toLocaleString()}`, 'Revenue']}
                />
                <Area type="monotone" dataKey="revenue" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#weeklyRevenueFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}
